import type {QueryParamFilterPriceChart} from '@/modules/common/context/QueryParams/models';
import type {RoomsType} from '@/modules/common/models/rooms';
import type {Price} from '@/redux/v1/models';

import {isValid as isValidDate, parseISO, formatISO} from 'date-fns';

import {Rooms} from '@/modules/common/models/rooms';

export type Schema<T> = {
  parse(input: string | null): T | null;
  stringify(value: T | null): string;
};

export const string = (): Schema<string> => ({
  parse(input) {
    return input !== null && input.trim().length > 0 ? input.trim() : null;
  },
  stringify(value) {
    return value ?? '';
  },
});

export const integer = ({
  validate = () => true,
}: {validate?: (value: number) => boolean} = {}): Schema<number> => ({
  parse(input) {
    const inputAsInteger = Number.parseInt(input ?? '', 10);
    return !Number.isNaN(inputAsInteger) && validate(inputAsInteger)
      ? inputAsInteger
      : null;
  },
  stringify(value) {
    return value === null ? '' : String(value);
  },
});

export const boolean = (): Schema<boolean> => ({
  parse(input) {
    if (input === 'on') {
      return true;
    }
    if (input === 'off') {
      return false;
    }
    return null;
  },
  stringify(value) {
    if (value === true) {
      return 'on';
    }
    if (value === false) {
      return 'off';
    }
    return '';
  },
});

export const array = <T>({
  schema,
  validate = () => true,
}: {
  schema: Schema<T>;
  validate?: (value: T) => boolean;
}): Schema<Array<T>> => ({
  parse(input) {
    return (input ?? '')
      .split(',')
      .map((value) => schema.parse(value) as T)
      .filter((value) => value !== null && validate(value));
  },
  stringify(value) {
    if (Array.isArray(value)) {
      return value
        .map((v) => schema.stringify(v))
        .filter((v) => v !== '')
        .join(',');
    }
    return '';
  },
});

export const enumerable = <T extends Record<string, string | number>>({
  _enum,
}: {
  _enum: T;
}): Schema<keyof T> => ({
  parse(input) {
    return _enum[input ?? ''] === undefined ? null : (input as keyof T);
  },
  stringify(value) {
    return String(value ?? '');
  },
});

export const isoDate = (): Schema<string> => ({
  parse(input) {
    const inputAsDate = parseISO(input?.trim() ?? '');
    return isValidDate(inputAsDate)
      ? formatISO(inputAsDate, {representation: 'date'})
      : null;
  },
  stringify(value) {
    return value ?? '';
  },
});

export const price = ({
  factor, // @FIXME not sure if this is the right word
  currency,
}: {
  factor: number;
  currency: string;
}): Schema<Price> => ({
  parse(input) {
    const valueAsInt = integer({
      validate: (value) => value >= 0,
    }).parse(input);

    return valueAsInt === null
      ? null
      : {
          amount: valueAsInt * factor,
          currency,
        };
  },
  stringify(value) {
    return value === null ? '' : String(value.amount / factor);
  },
});

// custom

export const rooms = (): Schema<RoomsType> => ({
  parse(input) {
    return Rooms.create(
      ...Array.from(Rooms.fromString(input || '')).filter(
        (room) => room.adultsCount > 0
      )
    );
  },
  stringify(value) {
    return value?.toString() || '';
  },
});

// @TODO add generic "object"-schema (and refactor this parameter)
export const filterPriceChart = (): Schema<QueryParamFilterPriceChart> => ({
  parse(input) {
    const params = input?.split(',') ?? [];
    const startDate = isoDate().parse(params[0]);
    const endDate = isoDate().parse(params[1]);
    const duration = integer().parse(params[2]);

    if (
      startDate !== null &&
      endDate !== null &&
      duration !== null &&
      startDate < endDate &&
      duration >= 0
    ) {
      return {startDate, endDate, duration};
    }
    return null;
  },
  stringify(value) {
    if (value !== null) {
      return `${value.startDate},${value.endDate},${value.duration}`;
    }
    return '';
  },
});
